import React from 'react';
import { Box, Typography } from '@mui/material';

const TUBE_HEX = {
  black: '#1a1a1a',
  red: '#c62828',
  white: '#ffffff',
  blue: '#1565c0',
  purple: '#6a1b9a',
  yellow: '#fdd835',
  orange: '#ef6c00',
  green: '#2e7d32'
};

const RIBBON_HEX = {
  white: '#ffffff',
  black: '#000000',
  metallicSilver: '#b0b0b0'
};

function contrastShadow(bgHex, fgHex) {
  const isLightBg = bgHex === '#ffffff' || bgHex === '#fdd835';
  const isLightFg = fgHex === '#ffffff' || fgHex === '#fdd835';
  if (isLightBg && isLightFg) {
    return '0 0 2px rgba(0,0,0,0.85), 0 1px 3px rgba(0,0,0,0.45)';
  }
  if (!isLightBg && fgHex === '#000000') {
    return '0 0 2px rgba(255,255,255,0.6), 0 1px 2px rgba(255,255,255,0.25)';
  }
  return 'none';
}

/**
 * Small preview: heat-shrink tube color as background, Helvetica text in ribbon/text color.
 */
function CustomHeatShrinkPreview({ value }) {
  const ch = value && typeof value === 'object' ? value : {};
  const bg = TUBE_HEX[ch.tubeColor] || TUBE_HEX.black;
  const fg = RIBBON_HEX[ch.ribbonColor] || RIBBON_HEX.white;
  const mode = ch.mode || 'text';
  const shadow = contrastShadow(bg, fg);

  const l1 = (ch.line1 || '').trim();
  const l2 = (ch.line2 || '').trim();
  const hasTwo = l1 && l2;
  const hasOne = (l1 && !l2) || (!l1 && l2);
  const single = l1 || l2;

  // ~50% larger than previous 0.95rem; logo branch unchanged. Single line scales up a bit more to fill the box.
  const fontSizeTwoLine = '1.4rem';
  const fontSizeOneLine = '1.55rem';

  const textStyleBase = {
    fontFamily: 'Helvetica, Arial, sans-serif',
    fontWeight: 600,
    color: fg,
    textAlign: 'center',
    textShadow: shadow,
    maxWidth: '100%',
    wordBreak: 'break-word'
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 320,
        minHeight: 120,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: bg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 1.25,
        py: 1.25,
        boxSizing: 'border-box',
        overflow: 'hidden'
      }}
    >
      {mode === 'logo' && ch.logo?.publicUrl ? (
        <Box
          component="img"
          src={ch.logo.publicUrl}
          alt="Logo preview"
          sx={{
            maxWidth: '92%',
            maxHeight: 72,
            objectFit: 'contain',
            display: 'block'
          }}
        />
      ) : hasTwo ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.1,
            width: '100%'
          }}
        >
          <Typography
            component="span"
            sx={{
              ...textStyleBase,
              fontSize: fontSizeTwoLine,
              lineHeight: 1.15
            }}
          >
            {l1}
          </Typography>
          <Typography
            component="span"
            sx={{
              ...textStyleBase,
              fontSize: fontSizeTwoLine,
              lineHeight: 1.15
            }}
          >
            {l2}
          </Typography>
        </Box>
      ) : hasOne ? (
        <Typography
          component="span"
          sx={{
            ...textStyleBase,
            fontSize: fontSizeOneLine,
            lineHeight: 1.1
          }}
        >
          {single}
        </Typography>
      ) : (
        <Typography
          component="span"
          sx={{
            ...textStyleBase,
            fontWeight: 400,
            opacity: 0.75,
            fontSize: '1.2rem'
          }}
        >
          Preview (enter text or upload logo)
        </Typography>
      )}
    </Box>
  );
}

export default CustomHeatShrinkPreview;
